import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin/contacts")({
  component: AdminContacts,
  ssr: false,
  head: () => ({
    meta: [
      { title: "Admin Contatti — Comm42 SA" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

type Submission = {
  id: string;
  name: string;
  email: string;
  company: string | null;
  message: string;
  created_at: string;
};

function AdminContacts() {
  const [ready, setReady] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [rows, setRows] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUserEmail(data.session?.user?.email ?? null);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setUserEmail(s?.user?.email ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("contact_submissions")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) setMsg(error.message);
    else setRows((data ?? []) as Submission[]);
    setLoading(false);
  }

  useEffect(() => {
    if (userEmail) load();
  }, [userEmail]);

  async function signIn(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setMsg(error.message);
    setBusy(false);
  }

  async function signOut() {
    await supabase.auth.signOut();
    setRows([]);
  }

  async function remove(id: string) {
    if (!confirm("Eliminare definitivamente questo messaggio?")) return;
    const { error } = await supabase.from("contact_submissions").delete().eq("id", id);
    if (error) setMsg(error.message);
    else setRows((r) => r.filter((x) => x.id !== id));
  }

  if (!ready) return null;

  if (!userEmail) {
    return (
      <div className="mx-auto max-w-md px-6 py-32">
        <h1 className="font-serif text-3xl">Admin — Login</h1>
        <form onSubmit={signIn} className="mt-8 space-y-4">
          <input
            type="email"
            required
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border-b border-border bg-transparent py-2 outline-none focus:border-primary"
          />
          <input
            type="password"
            required
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border-b border-border bg-transparent py-2 outline-none focus:border-primary"
          />
          <button
            disabled={busy}
            className="mt-4 bg-primary px-6 py-3 text-xs tracking-[0.3em] text-primary-foreground"
          >
            {busy ? "..." : "Entra"}
          </button>
          {msg && <p className="text-sm text-destructive">{msg}</p>}
        </form>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-24">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl">Messaggi dal form di contatto</h1>
          <p className="mt-1 text-sm text-muted-foreground">{userEmail}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={load} className="border border-border px-4 py-2 text-xs tracking-[0.2em]">
            Aggiorna
          </button>
          <button onClick={signOut} className="border border-border px-4 py-2 text-xs tracking-[0.2em]">
            Esci
          </button>
        </div>
      </div>

      {msg && <p className="mt-4 text-sm text-destructive">{msg}</p>}

      <div className="mt-8 space-y-4">
        {loading && <p className="text-sm text-muted-foreground">Caricamento...</p>}
        {!loading && rows.length === 0 && (
          <p className="text-sm text-muted-foreground">Nessun messaggio ricevuto.</p>
        )}
        {rows.map((r) => (
          <article key={r.id} className="rounded-lg border border-border/60 bg-card p-6">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h2 className="font-serif text-xl">{r.name}</h2>
                <p className="text-sm text-muted-foreground">
                  <a href={`mailto:${r.email}`} className="hover:text-primary">{r.email}</a>
                  {r.company ? ` — ${r.company}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <time className="text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleString("it-IT")}
                </time>
                <button
                  onClick={() => remove(r.id)}
                  className="text-xs text-destructive hover:underline"
                >
                  Elimina
                </button>
              </div>
            </div>
            <p className="mt-4 whitespace-pre-wrap text-sm">{r.message}</p>
          </article>
        ))}
      </div>
    </div>
  );
}